import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import {
	Button,
	Dialog,
	getLastSyncDisplay,
	getSourceIcon,
	getStatusBadge,
	Modal,
	SpinnerWithBackground,
	Text,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import { IntegrationWizard } from './IntegrationWizard'
import { SelectIntegrationTypeStep } from './SelectIntegrationTypeStep'
import { TriggerHistorySection } from './TriggerHistorySection'
import { TriggerWorkflowDialog } from './TriggerWorkflowDialog'
import { ConnectionsTab } from './tabs/ConnectionsTab'
import type { CrmProvider } from './types'
import { WebhookWizard } from './WebhookWizard'

type RouterOutput = inferRouterOutputs<AppRouter>
type CrmIntegration = RouterOutput['admin']['integrations']['crm']['getByOrganization'][number]

interface ConfigureDealTeamModalProps {
	isOpen: boolean
	onClose: () => void
	organizationId: string
	initialTab?: 'integrations' | 'connections'
}

type ModalView = 'list' | 'select-type' | 'wizard' | 'webhook-wizard'

const VIEW = {
	LIST: 'list',
	SELECT_TYPE: 'select-type',
	WIZARD: 'wizard',
	WEBHOOK_WIZARD: 'webhook-wizard',
} as const satisfies Record<string, ModalView>

const viewForProvider = (provider: CrmProvider): Extract<ModalView, 'wizard' | 'webhook-wizard'> =>
	provider === 'webhook' ? VIEW.WEBHOOK_WIZARD : VIEW.WIZARD

export function ConfigureDealTeamModal({
	isOpen,
	onClose,
	organizationId,
	initialTab = 'integrations',
}: ConfigureDealTeamModalProps) {
	const { showToast } = useToast()
	const queryClient = useQueryClient()
	const [activeTab, setActiveTab] = useState<'integrations' | 'connections'>(initialTab)

	// Sync tab if initialTab changes while open (e.g. via callback)
	useEffect(() => {
		if (isOpen) {
			setActiveTab(initialTab)
		}
	}, [initialTab, isOpen])
	const [view, setView] = useState<ModalView>('list')
	const [deleteTarget, setDeleteTarget] = useState<CrmIntegration | null>(null)
	const [editingIntegrationId, setEditingIntegrationId] = useState<string | null>(null)
	const [triggerTarget, setTriggerTarget] = useState<CrmIntegration | null>(null)
	const [resetCursorTarget, setResetCursorTarget] = useState<CrmIntegration | null>(null)

	const { data: integrations, isLoading } = useQuery({
		...trpc.admin.integrations.crm.getByOrganization.queryOptions({
			organizationId,
		}),
		enabled: isOpen && !!organizationId,
	})

	const pauseMutation = useMutation({
		...trpc.admin.integrations.crm.pause.mutationOptions(),
		onSuccess: () => {
			showToast('Integration paused')
			invalidateIntegrations()
		},
		onError: (err) => showToast(`Failed to pause: ${err.message}`, 'error'),
	})

	const resumeMutation = useMutation({
		...trpc.admin.integrations.crm.resume.mutationOptions(),
		onSuccess: () => {
			showToast('Integration resumed')
			invalidateIntegrations()
		},
		onError: (err) => {
			const friendlyMessages: Record<string, string> = {
				'Cannot resume: no field mappings configured': 'Please complete the Data Mapping step before activating.',
				'Cannot resume: no event mappings configured': 'Please configure event mappings before activating.',
				'Cannot resume: missing required field mappings':
					'Some required field mappings are missing. Please check the Data Mapping step.',
			}
			const missing = Object.keys(friendlyMessages).find((key) => err.message.includes(key))
			showToast(missing ? friendlyMessages[missing] : `Failed to activate: ${err.message}`, 'error')
		},
	})

	const deleteMutation = useMutation({
		...trpc.admin.integrations.crm.delete.mutationOptions(),
		onSuccess: () => {
			showToast('Integration deleted')
			setDeleteTarget(null)
			invalidateIntegrations()
		},
		onError: (err) => showToast(`Failed to delete: ${err.message}`, 'error'),
	})

	const resetSyncCursorMutation = useMutation({
		...trpc.admin.integrations.crm.resetSyncCursor.mutationOptions(),
		onSuccess: (data) => {
			showToast(
				data.lastSyncAt
					? `Sync cursor reset to ${data.lastSyncAt.slice(0, 10)} — next poll will re-evaluate every record modified since`
					: 'Sync cursor cleared — next poll will use the default 24h window',
			)
			setResetCursorTarget(null)
			invalidateIntegrations()
		},
		onError: (err) => showToast(`Failed to reset sync cursor: ${err.message}`, 'error'),
	})

	const invalidateIntegrations = () => {
		queryClient.invalidateQueries({
			queryKey: trpc.admin.integrations.crm.getByOrganization.queryKey({
				organizationId: organizationId as string,
			}),
		})
	}

	const handleClose = () => {
		setView(VIEW.LIST)
		setDeleteTarget(null)
		setEditingIntegrationId(null)
		onClose()
	}

	const handleWizardComplete = () => {
		setEditingIntegrationId(null)
		setView(VIEW.LIST)
	}

	const getActionButton = (integration: CrmIntegration) => {
		switch (integration.status) {
			case 'active':
				return (
					<Button
						variant='destructive'
						size='small'
						onClick={() => pauseMutation.mutate({ id: integration.id })}
						disabled={pauseMutation.isPending}
						className='w-[72px] justify-center'>
						Pause
					</Button>
				)
			case 'paused':
				return (
					<Button
						variant='primary'
						size='small'
						onClick={() => resumeMutation.mutate({ id: integration.id })}
						disabled={resumeMutation.isPending}
						className='w-[72px] justify-center'>
						Resume
					</Button>
				)
			case 'error':
				return (
					<Button
						variant='primary'
						size='small'
						onClick={() => resumeMutation.mutate({ id: integration.id })}
						disabled={resumeMutation.isPending}
						className='w-[72px] justify-center'>
						Retry
					</Button>
				)
			case 'pending_setup':
				return (
					<Button
						variant='primary'
						size='small'
						onClick={() => resumeMutation.mutate({ id: integration.id })}
						disabled={resumeMutation.isPending}
						className='w-[72px] justify-center'>
						Activate
					</Button>
				)
			default:
				return null
		}
	}

	const integrationList = integrations || []

	// CRM Wizard view
	if (view === 'wizard') {
		return (
			<IntegrationWizard
				isOpen={isOpen}
				onClose={handleClose}
				onComplete={handleWizardComplete}
				editingIntegrationId={editingIntegrationId || undefined}
			/>
		)
	}

	// Webhook Wizard view
	if (view === VIEW.WEBHOOK_WIZARD) {
		return (
			<WebhookWizard
				isOpen={isOpen}
				onClose={handleClose}
				onComplete={handleWizardComplete}
				editingIntegrationId={editingIntegrationId || undefined}
			/>
		)
	}

	// Select type view
	if (view === VIEW.SELECT_TYPE) {
		return (
			<Modal isOpen={isOpen} onClose={handleClose} title='Add New Integration' className='!w-[700px] md:!w-[800px]'>
				<SelectIntegrationTypeStep
					onSelect={(type) => {
						setEditingIntegrationId(null)
						if (type === 'crm') {
							setView(VIEW.WIZARD)
						} else if (type === 'webhook') {
							setView(VIEW.WEBHOOK_WIZARD)
						}
					}}
					onCancel={() => setView(VIEW.LIST)}
				/>
			</Modal>
		)
	}

	// List view (default)
	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={handleClose}
				title='Configure DealTeam Module'
				className='!w-[700px] md:!w-[800px]'>
				<div className='flex flex-col gap-4'>
					<div className='flex border-b border-neutral-200'>
						<button
							type='button'
							className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
								activeTab === 'integrations'
									? 'border-primary-600 text-primary-600'
									: 'border-transparent text-neutral-500 hover:text-neutral-700'
							}`}
							onClick={() => setActiveTab('integrations')}>
							Integrations (Automations)
						</button>
						<button
							type='button'
							className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
								activeTab === 'connections'
									? 'border-primary-600 text-primary-600'
									: 'border-transparent text-neutral-500 hover:text-neutral-700'
							}`}
							onClick={() => setActiveTab('connections')}>
							Connections
						</button>
					</div>

					{activeTab === 'connections' ? (
						<ConnectionsTab organizationId={organizationId} />
					) : (
						<>
							<Text variant='medium' weight='semibold'>
								CRM Integrations
							</Text>

							{isLoading ? (
								<div className='py-12'>
									<SpinnerWithBackground backgroundClass='opacity-100' />
								</div>
							) : integrationList.length === 0 ? (
								<div className='py-12 flex flex-col items-center justify-center border-2 border-dashed border-neutral-300 rounded-lg'>
									<Text variant='medium' weight='semibold' className='text-neutral-600 mb-2'>
										No Integrations Configured
									</Text>
									<Text variant='small' className='text-neutral-500 mb-4 text-center max-w-md'>
										Add a CRM integration to automatically launch deal team cycles when deals reach key milestones.
									</Text>
									<Button variant='primary' onClick={() => setView(VIEW.SELECT_TYPE)}>
										+ Add First Integration
									</Button>
								</div>
							) : (
								<>
									{/* Table */}
									<div className='border border-neutral-200 rounded-lg overflow-hidden bg-white shadow-sm'>
										{/* Table Header */}
										<div className='grid grid-cols-[1fr_100px_120px_90px_260px] gap-4 px-6 py-3 bg-neutral-50 border-b border-neutral-200'>
											<Text
												variant='xsmall'
												weight='semibold'
												className='text-neutral-500 uppercase tracking-wider whitespace-nowrap'>
												Name
											</Text>
											<Text
												variant='xsmall'
												weight='semibold'
												className='text-neutral-500 uppercase tracking-wider whitespace-nowrap'>
												Source
											</Text>
											<Text
												variant='xsmall'
												weight='semibold'
												className='text-neutral-500 uppercase tracking-wider whitespace-nowrap'>
												Last Sync
											</Text>
											<Text
												variant='xsmall'
												weight='semibold'
												className='text-neutral-500 uppercase tracking-wider whitespace-nowrap'>
												Status
											</Text>
											<Text
												variant='xsmall'
												weight='semibold'
												className='text-neutral-500 uppercase tracking-wider whitespace-nowrap text-right'>
												Actions
											</Text>
										</div>

										{/* Table Rows */}
										{integrationList.map((integration) => (
											<div
												key={integration.id}
												className='grid grid-cols-[1fr_100px_120px_90px_260px] gap-4 px-6 py-4 items-center border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 transition-colors'>
												{/* Name */}
												<Tooltip>
													<TooltipTrigger asChild>
														<div className='min-w-0'>
															<Text variant='small' weight='semibold' className='truncate block text-neutral-900'>
																{integration.name}
															</Text>
														</div>
													</TooltipTrigger>
													<TooltipContent>{integration.name}</TooltipContent>
												</Tooltip>

												{/* Source */}
												{getSourceIcon(integration.provider)}

												{/* Last Sync */}
												{getLastSyncDisplay(integration)}

												{/* Status */}
												<div>{getStatusBadge(integration.status)}</div>

												{/* Actions */}
												<div className='flex items-center justify-end gap-3'>
													{/* Trigger */}
													{integration.status === 'active' && (
														<Button variant='secondary' size='small' onClick={() => setTriggerTarget(integration)}>
															Trigger
														</Button>
													)}

													{/* Tools (Reset Cursor / Edit / Delete) */}
													<div className='flex items-center gap-1'>
														{/* Reset Sync Cursor */}
														<button
															type='button'
															onClick={() => setResetCursorTarget(integration)}
															disabled={resetSyncCursorMutation.isPending}
															className='p-1.5 rounded-md hover:bg-neutral-200 text-neutral-400 hover:text-neutral-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
															title='Reset Sync Cursor — next poll re-evaluates all records (sandbox testing escape hatch)'>
															<svg
																className='w-4 h-4'
																fill='none'
																viewBox='0 0 24 24'
																stroke='currentColor'
																strokeWidth={1.5}
																aria-hidden='true'>
																<path
																	strokeLinecap='round'
																	strokeLinejoin='round'
																	d='M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99'
																/>
															</svg>
														</button>

														{/* Edit */}
														<button
															type='button'
															onClick={() => {
																setEditingIntegrationId(integration.id)
																setView(viewForProvider(integration.provider as CrmProvider))
															}}
															className='p-1.5 rounded-md hover:bg-neutral-200 text-neutral-400 hover:text-neutral-700 transition-colors cursor-pointer'
															title='Edit Configuration'>
															<svg
																className='w-4 h-4'
																fill='none'
																viewBox='0 0 24 24'
																stroke='currentColor'
																strokeWidth={1.5}
																aria-hidden='true'>
																<path
																	strokeLinecap='round'
																	strokeLinejoin='round'
																	d='M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10'
																/>
															</svg>
														</button>

														{/* Delete */}
														<button
															type='button'
															onClick={() => setDeleteTarget(integration)}
															className='p-1.5 rounded-md hover:bg-cod-gray-100 text-neutral-400 hover:text-red-600 transition-colors cursor-pointer'
															title='Delete Integration'>
															<svg
																className='w-4 h-4'
																fill='none'
																viewBox='0 0 24 24'
																stroke='currentColor'
																strokeWidth={1.5}
																aria-hidden='true'>
																<path
																	strokeLinecap='round'
																	strokeLinejoin='round'
																	d='M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0'
																/>
															</svg>
														</button>
													</div>

													{/* Status Action (Fixed Width Container to prevent shifting) */}
													<div className='w-[90px] flex justify-end'>{getActionButton(integration)}</div>
												</div>
											</div>
										))}
									</div>

									{/* Add New button */}
									<div className='flex justify-end pt-2'>
										<Button variant='primary' size='medium' onClick={() => setView(VIEW.SELECT_TYPE)}>
											+ Add New Integration
										</Button>
									</div>
								</>
							)}
						</>
					)}

					{activeTab === 'integrations' && !isLoading && <TriggerHistorySection organizationId={organizationId} />}

					{/* Footer buttons */}
					<div className='flex justify-end gap-3 pt-4 border-t border-neutral-100 mt-2'>
						<Button variant='secondary' onClick={handleClose}>
							Cancel
						</Button>
						<Button variant='primary' onClick={handleClose}>
							Done
						</Button>
					</div>
				</div>
			</Modal>

			{/* Delete Confirmation */}
			<Dialog
				isOpen={!!deleteTarget}
				onClose={() => setDeleteTarget(null)}
				onConfirm={() => deleteTarget && deleteMutation.mutate({ id: deleteTarget.id })}
				title='Delete Integration'
				message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone. Active webhooks will stop processing immediately.`}
				type='delete'
				confirmLabel='Delete Integration'
				requireConfirmText
				confirmText='DELETE'
				isConfirmLoading={deleteMutation.isPending}
			/>

			{/* Reset Sync Cursor Confirmation. Default poll behavior is "since lastSyncAt or 24h ago",
			    so a no-op reset (lastSyncAt = NULL) gives the safe 24h-fallback. The dangerous case
			    is full backfill, which we send as an explicit `since: epoch` only after this confirm
			    so it can't be triggered by a single misclick. */}
			<Dialog
				isOpen={!!resetCursorTarget}
				onClose={() => setResetCursorTarget(null)}
				onConfirm={() =>
					resetCursorTarget &&
					resetSyncCursorMutation.mutate({ id: resetCursorTarget.id, since: '1970-01-01T00:00:00.000Z' })
				}
				title='Reset Sync Cursor'
				message={`Force the next poll to re-evaluate every record in "${resetCursorTarget?.name}" — including ones already processed. Use this only for sandbox testing; in a production tenant this can launch hundreds of cycles in a single poll.`}
				type='confirm'
				confirmLabel='Reset & re-evaluate all records'
				requireConfirmText
				confirmText='RESET'
				isConfirmLoading={resetSyncCursorMutation.isPending}
			/>

			{triggerTarget && (
				<TriggerWorkflowDialog
					isOpen={!!triggerTarget}
					onClose={() => setTriggerTarget(null)}
					organizationId={organizationId}
					integration={triggerTarget}
				/>
			)}
		</>
	)
}
